<template>
  <!-- One purchasable plan (hourly / daily / weekly / monthly) on the Buy Proxy page and the admin
       Proxy Stock page. The selected card is the only one with the accent border and the solid
       purchase button. -->
  <article
    class="package-plan-card"
    :class="{ 'is-selected': selected && !disabled, 'is-disabled': disabled }"
    role="button"
    :aria-pressed="selected"
    tabindex="0"
    @click="!disabled && emit('select')"
    @keydown.enter.prevent="!disabled && emit('select')"
    @keydown.space.prevent="!disabled && emit('select')"
  >
    <span v-if="selected && !disabled" class="package-plan-card__badge">Selected</span>

    <div class="package-plan-card__top">
      <p class="package-plan-card__period">{{ periodLabel }}</p>
      <span v-if="savingText" class="package-plan-card__saving">{{ savingText }}</span>
    </div>
    <p class="package-plan-card__price">${{ priceText }}</p>
    <p class="package-plan-card__perday">{{ perText }}</p>

    <ul class="package-plan-card__features">
      <li><v-icon icon="mdi-database-outline" size="18"></v-icon><span>{{ dataText }}</span></li>
      <li><v-icon icon="mdi-refresh" size="18"></v-icon><span>{{ rotationText }}</span></li>
      <li><v-icon icon="mdi-shield-check-outline" size="18"></v-icon><span>HTTP and SOCKS5</span></li>
      <li v-if="maxSpeed" class="package-plan-card__speed"><v-icon icon="mdi-speedometer" size="18"></v-icon><span>Up to {{ maxSpeed }} Mbps</span></li>
      <li v-if="offers('openvpn')"><img :src="openVpnLogo" class="package-plan-card__vpn" alt="" /><span>OpenVPN</span></li>
      <li v-if="offers('wireguard')"><img :src="wireGuardLogo" class="package-plan-card__vpn package-plan-card__vpn--wg" alt="" /><span>WireGuard</span></li>
      <li v-if="offers('os_spoofing')"><v-icon icon="mdi-monitor-cellphone" size="18"></v-icon><span>OS / IP fingerprint spoofing</span></li>
      <li v-if="offers('udp')"><v-icon icon="mdi-swap-horizontal" size="18"></v-icon><span>SOCKS5 UDP</span></li>
    </ul>

    <button
      type="button"
      class="package-plan-card__action"
      :class="{ 'is-selected': selected && !disabled }"
      :disabled="disabled"
      @click.stop="emit('select'); emit('action')"
    >
      {{ disabled ? 'Out of stock' : selected ? `Purchase ${priceText}` : 'Select' }}
    </button>
  </article>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";
import openVpnLogo from "@/assets/openVPN.png";
import wireGuardLogo from "@/assets/wireguard.svg";

const props = defineProps({
  plan: { type: Object, required: true },
  period: { type: String, required: true },          // 'hourly' | 'daily' | 'weekly' | 'monthly'
  selected: { type: Boolean, default: false },
  dailyPrice: { type: Number, default: 0 },          // the location's daily plan price, for the saving note
  osSpoofing: { type: Boolean, default: false },     // the stock shown was filtered to these features
  udp: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },      // no stock at this location: prices shown, nothing to buy
});

const emit = defineEmits(["select", "action"]);

const UNIT_DAYS = { DAY: 1, WEEK: 7, MONTH: 30 };
const PERIOD_UNIT = { hourly: 'HOUR', daily: 'DAY', weekly: 'WEEK', monthly: 'MONTH' };

const price = computed(() => Number(props.plan?.price) || 0);
const duration = computed(() => Math.max(1, Number(props.plan?.duration) || 1));
const unit = computed(() => String(props.plan?.time_unit || PERIOD_UNIT[props.period] || '').toUpperCase());
const days = computed(() => (UNIT_DAYS[unit.value] || 0) * duration.value);   // 0 for hourly plans
const isProxySmart = computed(() => String(props.plan?.type || '').toUpperCase() === 'PROXYSMART');
// ProProxyUI plans list what their server offers (Add / Edit server > Features); the other types keep the old
// rule: OpenVPN on ProxySmart, OS spoofing / UDP when the Buy page filter asked for them
const offers = (name) => Array.isArray(props.plan?.features)
  ? props.plan.features.includes(name)
  : name === 'openvpn' ? isProxySmart.value : name === 'os_spoofing' ? props.osSpoofing : name === 'udp' ? props.udp : false;
const maxSpeed = computed(() => Number(props.plan?.max_speed_mbps) || 0);
const money = (v) => (Math.round(v * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

const periodLabel = computed(() => ({ hourly: 'Hourly', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' })[props.period] || props.period);
const priceText = computed(() => money(price.value));
const perText = computed(() => {
  if (unit.value === 'HOUR') return duration.value > 1 ? `for ${duration.value} hours` : 'per hour';
  if (days.value > 1) return `$${(price.value / days.value).toFixed(2)} per day`;
  return 'per day';
});
// saving against buying the same days one day at a time
const savingText = computed(() => {
  if (!props.dailyPrice || days.value <= 1) return '';
  const pct = Math.round((1 - price.value / (props.dailyPrice * days.value)) * 100);
  return pct >= 5 ? `Save ${pct}%` : '';
});
const dataText = computed(() => props.plan?.data_limit ? `${props.plan.data_limit} GB data per day` : 'Unlimited data');
// the enforced cooldown between IP changes is min_time_change_ip (minutes); rotation_time is an older display field
const rotationText = computed(() => {
  const mins = Number(props.plan?.min_time_change_ip) || Number(props.plan?.rotation_time) || 0;
  return mins > 0 ? `IP change every ${mins} min` : 'IP change on demand';
});
</script>

<style scoped>
.package-plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 12px 12px 10px;
  border: 1px solid var(--ppc-line);
  border-radius: 10px;
  background: var(--ppc-card);
  color: var(--ppc-ink);
  text-align: left;
  cursor: pointer;
  outline: none;
  transition: border-color 150ms ease, transform 150ms ease;
}
.package-plan-card:hover,
.package-plan-card:focus-visible { border-color: var(--ppc-pri-hover); transform: translateY(-1px); }
.package-plan-card.is-selected { border: 2px solid var(--ppc-pri); padding: 11px 11px 9px; }

.package-plan-card__badge {
  position: absolute; top: -9px; left: 10px;
  padding: 1px 8px; border-radius: 999px;
  background: var(--ppc-pri); color: #ffffff;
  font-size: 11px; font-weight: 600; line-height: 15px;
}
.package-plan-card__top { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.package-plan-card__period { margin: 0; font-size: 11.5px; letter-spacing: 0.04em; text-transform: uppercase; color: var(--ppc-dim); }
.package-plan-card__saving { font-size: 11.5px; color: var(--ppc-ok); white-space: nowrap; }
.package-plan-card__price { margin: 4px 0 0; font-size: 22px; line-height: 1.1; font-weight: 600; color: var(--ppc-ink); }
.package-plan-card__perday { margin: 0 0 8px; font-size: 12px; color: var(--ppc-dim); }

.package-plan-card__features {
  list-style: none; margin: 0; padding: 8px 0 0;
  border-top: 1px solid var(--ppc-line);
  display: grid; gap: 4px;
  font-size: 12px; color: var(--ppc-dim);
  flex: 1;
}
.package-plan-card__features li { display: flex; align-items: center; gap: 6px; line-height: 1.3; }
.package-plan-card__features .v-icon { color: var(--ppc-ok); font-size: 16px !important; }
.package-plan-card__vpn { height: 16px; width: 16px; object-fit: contain; }
/* a capped server: amber, so it is not missed among the grey lines */
.package-plan-card__speed, .package-plan-card__speed .v-icon { color: #f59e0b !important; font-weight: 700; }
/* the WireGuard mark is dark red: a white disc keeps it readable on the dark card */
.package-plan-card__vpn--wg { background: #ffffff; border-radius: 50%; padding: 1px; }

.package-plan-card__action {
  margin-top: 10px; width: 100%; min-height: 32px;
  border: 1px solid var(--ppc-line); border-radius: 7px;
  background: transparent; color: var(--ppc-ink);
  font-size: 13px; font-weight: 500; padding: 6px 10px; text-align: center;
  transition: background-color 150ms ease, border-color 150ms ease;
}
.package-plan-card__action:hover { border-color: var(--ppc-pri-hover); }
.package-plan-card__action.is-selected { border-color: var(--ppc-pri); background: var(--ppc-pri); color: #ffffff; font-weight: 600; }
.package-plan-card__action.is-selected:hover { background: var(--ppc-pri-hover); }
.package-plan-card.is-disabled { cursor: default; }
.package-plan-card.is-disabled:hover { transform: none; border-color: var(--ppc-line); }
.package-plan-card__action:disabled { cursor: not-allowed; color: var(--ppc-dim); }
</style>

<style>
/* theme colours, unscoped so body.dark reaches the card (Vue's scoped :global() drops the rest of
   the selector). Dark values are the site's own: .dark .card / .dark .bg-white */
.package-plan-card {
  --ppc-line: #d9dee5;
  --ppc-card: #ffffff;
  --ppc-ink: #1f2937;
  --ppc-dim: #6b7280;
  --ppc-pri: #0d6efd;
  --ppc-pri-hover: #0b5ed7;
  --ppc-ok: #198754;
}
body.dark .package-plan-card {
  --ppc-line: #45445e;
  --ppc-card: #2b2a3d;
  --ppc-ink: #ffffff;
  --ppc-dim: #bcbbc7;
  --ppc-ok: #4ade80;
}
</style>
